import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import { invoicesApi } from "@/services/api";
import { exportToCSV, exportToExcel, exportToPDF, formatDate, formatCurrency } from "@/utils/export";
import { useRoleGuard, ROLE_GROUPS } from "@/hooks/useRoleGuard";
import { usePagePermission } from "@/hooks/usePagePermission";

type BillingRecord = {
  id: number;
  invoiceNo: string;
  invoice_no?: string;
  doc_no?: string;
  jobNo: string;
  job_no?: string;
  client: string;
  client_name?: string;
  amount: number;
  subtotal?: number;
  tax: number;
  tax_amount?: number;
  total: number;
  status: string;
  dueDate: string;
  due_date?: string;
  paidDate: string | null;
  paid_date?: string | null;
  type: string;
};

/** Strip time portion from ISO datetime strings for display / date inputs */
function toDateStr(val: string | null | undefined): string {
  if (!val) return "";
  const match = val.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : val;
}

function normalizeRecord(r: BillingRecord): BillingRecord {
  return {
    ...r,
    invoiceNo: r.invoiceNo || r.invoice_no || r.doc_no || "",
    jobNo: r.jobNo || r.job_no || "",
    client: r.client || r.client_name || "",
    amount: r.amount ?? r.subtotal ?? 0,
    tax: r.tax ?? r.tax_amount ?? 0,
    dueDate: toDateStr(r.dueDate || r.due_date),
    paidDate: toDateStr(r.paidDate ?? r.paid_date ?? null) || null,
    type: r.type ? r.type.charAt(0).toUpperCase() + r.type.slice(1) : "Invoice",
  };
}

const statusColors: Record<string, { bg: string; text: string }> = {
  "Paid":             { bg: "#f0fdf4", text: "#10b981" },
  "Pending":          { bg: "#fffbeb", text: "#d97706" },
  "Overdue":          { bg: "#fef2f2", text: "#ef4444" },
  "Approved":         { bg: "#fef3c7", text: "#b45309" },
  "Pending Approval": { bg: "#fffbeb", text: "#d97706" },
  "Draft":            { bg: "#f3f4f6", text: "#6b7280" },
};

interface MarkPaidModalProps {
  record: BillingRecord;
  onConfirm: (paidDate: string, paymentRef: string) => void;
  onCancel: () => void;
}

function MarkPaidModal({ record, onConfirm, onCancel }: MarkPaidModalProps) {
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split("T")[0]);
  const [paymentRef, setPaymentRef] = useState("");
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-money-rupee-circle-line text-xl text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Mark as Paid</h3>
              <p className="text-xs text-gray-500 mt-0.5">{record.invoiceNo} · ₹{record.total.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Payment Date *</label>
            <input
              type="date"
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Payment Reference (UTR / Cheque No.)</label>
            <input
              type="text"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              placeholder="e.g. NEFT2026040412345"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(paidDate, paymentRef)}
            disabled={!paidDate}
            className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-bold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap disabled:opacity-60"
          >
            <i className="ri-checkbox-circle-line mr-1.5" />Confirm Payment
          </button>
        </div>
      </div>
    </div>
  );
}

interface ApproveModalProps {
  record: BillingRecord;
  onConfirm: () => void;
  onCancel: () => void;
}

function ApproveModal({ record, onConfirm, onCancel }: ApproveModalProps) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-circle-line text-xl text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Approve {record.type}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{record.invoiceNo}</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
            {[
              { label: "Document", value: record.invoiceNo },
              { label: "Client",   value: record.client },
              { label: "Job Ref",  value: record.jobNo },
              { label: "Amount",   value: `₹${record.total.toLocaleString()}` },
              { label: "Due Date", value: record.dueDate },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{row.label}</span>
                <span className="text-xs font-semibold text-gray-800">{row.value}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">
            Approving this {record.type.toLowerCase()} will mark it as <strong>Approved</strong> and notify the client.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-bold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap">
            <i className="ri-checkbox-circle-line mr-1.5" />Approve Now
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Dynamic permission — reads from localStorage saved by Admin Roles page
  // Default: office staff and above can view billing
  const { hasPermission: canViewBilling, user, loading: permLoading } = usePagePermission("Billing", "view", true);
  const canEdit = user ? ROLE_GROUPS.managers.includes(user.role) : false;
  const [tab, setTab] = useState(searchParams.get("tab") || "All");
  const [records, setRecords] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [approveTarget, setApproveTarget] = useState<BillingRecord | null>(null);
  const [markPaidTarget, setMarkPaidTarget] = useState<BillingRecord | null>(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const showToast = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3500);
  };

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const data = await invoicesApi.getAll({ limit: 100 });
      const raw: BillingRecord[] = Array.isArray(data) ? data : data.data ?? [];
      setRecords(raw.map(normalizeRecord));
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Sync URL params with filter state
  useEffect(() => {
    const params: Record<string, string> = {};
    if (tab !== "All") params.tab = tab;
    setSearchParams(params, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const tabs = ["All", "Quotation", "Proforma", "Invoice"];
  const filtered = tab === "All" ? records : records.filter((i) => i.type.toLowerCase() === tab.toLowerCase());

  const totalRevenue = records.filter((i) => i.status === "Paid").reduce((a, b) => a + b.total, 0);
  const pending = records.filter((i) => i.status === "Pending" || i.status === "Pending Approval").reduce((a, b) => a + b.total, 0);
  const overdue = records.filter((i) => i.status === "Overdue").reduce((a, b) => a + b.total, 0);
  const pendingApprovalCount = records.filter((i) => i.status === "Pending Approval").length;

  const confirmApprove = async () => {
    if (!approveTarget) return;
    try {
      await invoicesApi.updateStatus(approveTarget.id, "Approved");
    } catch {
      // Optimistic
    }
    setRecords((prev) => prev.map((r) => r.id === approveTarget.id ? { ...r, status: "Approved" } : r));
    showToast(`${approveTarget.type} ${approveTarget.invoiceNo} approved`);
    setApproveTarget(null);
  };

  const confirmMarkPaid = async (paidDate: string, paymentRef: string) => {
    if (!markPaidTarget) return;
    try {
      await invoicesApi.updateStatus(markPaidTarget.id, "Paid", markPaidTarget.total, paymentRef, paidDate);
    } catch {
      // Optimistic
    }
    setRecords((prev) =>
      prev.map((r) => r.id === markPaidTarget.id ? { ...r, status: "Paid", paidDate } : r)
    );
    showToast(`${markPaidTarget.invoiceNo} marked as paid`);
    setMarkPaidTarget(null);
  };

  const getExportData = () =>
    filtered.map((r) => ({
      "Invoice / Quote No.": r.invoiceNo,
      "Type": r.type,
      "Client": r.client,
      "Job No.": r.jobNo,
      "Subtotal": formatCurrency(r.amount),
      "GST": formatCurrency(r.tax),
      "Total": formatCurrency(r.total),
      "Status": r.status,
      "Due Date": formatDate(r.dueDate),
      "Paid Date": r.paidDate ? formatDate(r.paidDate) : "",
    }));

  const handleExportCSV = async () => {
    setExporting(true);
    exportToCSV(getExportData(), `billing-${tab.toLowerCase()}-${new Date().toISOString().slice(0, 10)}`);
    setExporting(false);
    setExportMenuOpen(false);
    showToast("Exported as CSV");
  };

  const handleExportExcel = async () => {
    setExporting(true);
    await exportToExcel(getExportData(), `billing-${tab.toLowerCase()}-${new Date().toISOString().slice(0, 10)}`, "Billing Records");
    setExporting(false);
    setExportMenuOpen(false);
    showToast("Exported as Excel");
  };

  const handleExportPDF = () => {
    exportToPDF("billing-table", `billing-${tab.toLowerCase()}-${new Date().toISOString().slice(0, 10)}`, `Billing & Payments — ${tab}`);
    setExportMenuOpen(false);
    showToast("Opening print dialog...");
  };

  if (!permLoading && user && !canViewBilling) {
    return (
      <ERPLayout title="Billing & Payments">
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-2-line text-3xl text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-sm text-gray-500 max-w-sm mb-1">
            Your role <strong className="text-gray-700">({user.role})</strong> does not have permission to view <strong>Billing</strong>.
          </p>
          <p className="text-xs text-gray-400 mb-6">Contact your administrator to request access.</p>
          <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 px-4 py-2 rounded-xl border border-gray-100">
            <i className="ri-shield-keyhole-line" />
            Permissions managed in <strong className="text-gray-600 ml-1">Admin Panel → Roles &amp; Permissions</strong>
          </div>
        </div>
      </ERPLayout>
    );
  }

  return (
    <ERPLayout title="Billing & Payments">
      {successMsg && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-amber-600 text-white text-sm px-4 py-2.5 rounded-xl font-medium">
          <i className="ri-checkbox-circle-line" />{successMsg}
        </div>
      )}

      {approveTarget && (
        <ApproveModal record={approveTarget} onConfirm={confirmApprove} onCancel={() => setApproveTarget(null)} />
      )}
      {markPaidTarget && (
        <MarkPaidModal record={markPaidTarget} onConfirm={confirmMarkPaid} onCancel={() => setMarkPaidTarget(null)} />
      )}

      <div className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Billing &amp; Payments</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {loading ? "Loading..." : `${records.length} total records`}
              {pendingApprovalCount > 0 && (
                <span className="ml-2 text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-semibold">
                  {pendingApprovalCount} pending approval
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchRecords}
              className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line text-sm" /></span>Refresh
            </button>

            {/* Export dropdown */}
            <div className="relative">
              <button
                onClick={() => setExportMenuOpen((v) => !v)}
                disabled={exporting || filtered.length === 0}
                className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={exporting ? "ri-loader-4-line animate-spin text-sm" : "ri-download-2-line text-sm"} />
                </span>
                Export
                <i className="ri-arrow-down-s-line text-xs" />
              </button>
              {exportMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-100 rounded-xl z-20 overflow-hidden">
                  <button
                    onClick={handleExportCSV}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-text-line text-amber-600" /></span>
                    Export CSV
                  </button>
                  <button
                    onClick={handleExportExcel}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-excel-line text-amber-600" /></span>
                    Export Excel
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-pdf-line text-red-500" /></span>
                    Export PDF
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => navigate("/billing/quotation/new")}
              className="flex items-center gap-2 px-4 py-2 border border-amber-500 text-amber-600 rounded-lg text-sm font-medium hover:bg-amber-50 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-text-line text-sm" /></span>New Quotation
            </button>
            <button
              onClick={() => navigate("/billing/invoice/new")}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></span>New Invoice
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Collected",  value: `₹${(totalRevenue / 100000).toFixed(1)}L`, icon: "ri-money-rupee-circle-line", color: "#10b981", bg: "#ecfdf5", query: "tab=Invoice" },
            { label: "Pending Amount",   value: `₹${(pending / 100000).toFixed(1)}L`,      icon: "ri-time-line",              color: "#d97706", bg: "#fffbeb", query: "tab=Invoice" },
            { label: "Overdue Amount",   value: `₹${(overdue / 100000).toFixed(1)}L`,      icon: "ri-alarm-warning-line",     color: "#ef4444", bg: "#fef2f2", query: "tab=Invoice" },
            { label: "Pending Approval", value: pendingApprovalCount,                       icon: "ri-checkbox-circle-line",   color: "#d97706", bg: "#fffbeb", query: "tab=Quotation" },
          ].map((s) => (
            <div
              key={s.label}
              onClick={() => navigate(`/billing?${s.query}`)}
              className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4 hover:border-amber-200 hover:shadow-sm transition-all cursor-pointer group"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: s.bg }}>
                <i className={`${s.icon} text-xl`} style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 group-hover:text-amber-700 transition-colors">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pending Approval Banner */}
        {pendingApprovalCount > 0 && (
          <div className="mb-4 flex items-center gap-3 bg-violet-50 border border-violet-200 rounded-xl px-5 py-3">
            <span className="w-8 h-8 flex items-center justify-center bg-violet-100 rounded-lg flex-shrink-0">
              <i className="ri-checkbox-circle-line text-violet-600" />
            </span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-violet-800">
                {pendingApprovalCount} document{pendingApprovalCount > 1 ? "s" : ""} awaiting your approval
              </p>
              <p className="text-xs text-violet-600 mt-0.5">Click the <strong>Approve</strong> button on any row to approve</p>
            </div>
            <button
              onClick={() => setTab("Quotation")}
              className="text-xs font-semibold text-violet-700 bg-violet-100 hover:bg-violet-200 px-3 py-1.5 rounded-lg cursor-pointer transition-all whitespace-nowrap"
            >
              Show Quotations
            </button>
          </div>
        )}

        {/* Table */}
        <div id="billing-table" className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center gap-1 p-3 border-b border-gray-100 bg-gray-50">
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${tab === t ? "bg-white text-gray-900 border border-gray-200" : "text-gray-500 hover:text-gray-700"}`}
              >
                {t}
                {t === "Quotation" && pendingApprovalCount > 0 && (
                  <span className="ml-1.5 text-xs bg-violet-500 text-white px-1.5 py-0.5 rounded-full font-bold">{pendingApprovalCount}</span>
                )}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line text-3xl text-amber-500 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Invoice / Quote No.</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Job No.</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Due Date</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((inv) => {
                    const sc = statusColors[inv.status] || { bg: "#f3f4f6", text: "#6b7280" };
                    const isPendingApproval = inv.status === "Pending Approval";
                    const isPending = inv.status === "Pending" || inv.status === "Overdue";
                    return (
                      <tr
                        key={inv.id}
                        className={`border-b border-gray-50 transition-colors ${isPendingApproval ? "bg-violet-50/30 hover:bg-violet-50/60" : "hover:bg-gray-50"}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-gray-800">{inv.invoiceNo}</span>
                            {isPendingApproval && <span className="w-2 h-2 bg-violet-500 rounded-full flex-shrink-0" />}
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className="text-sm text-gray-700">{inv.client}</span></td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{inv.jobNo}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{inv.type}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="text-sm font-bold text-gray-900">₹{inv.total.toLocaleString()}</p>
                          <p className="text-xs text-gray-400">+GST ₹{inv.tax.toLocaleString()}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.text }}>{inv.status}</span>
                        </td>
                        <td className="px-4 py-3 text-center hidden lg:table-cell">
                          <span className="text-xs text-gray-600">{inv.dueDate}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1.5">
                            {isPendingApproval && (
                              <button
                                onClick={() => setApproveTarget(inv)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 cursor-pointer whitespace-nowrap transition-all"
                              >
                                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-checkbox-circle-line text-xs" /></span>
                                Approve
                              </button>
                            )}
                            {isPending && inv.type === "Invoice" && (
                              <button
                                onClick={() => setMarkPaidTarget(inv)}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 cursor-pointer whitespace-nowrap"
                              >
                                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-money-rupee-circle-line text-xs" /></span>
                                Mark Paid
                              </button>
                            )}
                            {inv.type === "Invoice" && !isPending && (
                              <button
                                onClick={() => navigate(`/billing/invoice/${inv.invoiceNo}/print`)}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 cursor-pointer whitespace-nowrap"
                              >
                                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-file-text-line text-xs" /></span>
                                View Invoice
                              </button>
                            )}
                            {inv.type === "Quotation" && !isPendingApproval && (
                              <button
                                onClick={() => navigate(`/billing/quotation/${inv.invoiceNo}/print`)}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 cursor-pointer whitespace-nowrap"
                              >
                                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-file-list-3-line text-xs" /></span>
                                View Quote
                              </button>
                            )}
                            {inv.type === "Quotation" && isPendingApproval && (
                              <button
                                onClick={() => navigate(`/billing/quotation/${inv.invoiceNo}/print`)}
                                className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 cursor-pointer whitespace-nowrap"
                              >
                                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-eye-line text-xs" /></span>
                                Preview
                              </button>
                            )}
                            {(inv.type === "Proforma" || inv.type === "Receipt") && (
                              <button className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer">
                                <i className="ri-eye-line text-sm" />
                              </button>
                            )}
                            <button className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer">
                              <i className="ri-more-2-line text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-16 text-gray-400">
                        <i className="ri-file-list-3-line text-3xl text-gray-200 block mb-2" />
                        <p className="text-sm">No records found</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </ERPLayout>
  );
}
